<script setup lang="ts">

import { computed } from 'vue'
import { gameCharacters, goldCharacterId } from '@/partners/lingo/lingo-giveaway/config/characters'
import LingoGiveawayGameCounter from '@/partners/lingo/lingo-giveaway/components/LingoGiveawayGameCounter.vue'
import { useLingoGiveaway, winPrize } from '@/partners/lingo/lingo-giveaway/composables/lingo-giveaway'
import type { GACHA_PRIZE } from '@/partners/lingo/lingo-giveaway/types/shared/gacha'
import LingoGiveawayEggWithCharacter from '@/partners/lingo/lingo-giveaway/components/LingoGiveawayEggWithCharacter.vue'
import { formatNumberFullTruncate } from '@/helpers/utils'

const emit = defineEmits<{
  (e: 'showCard', characterId: GACHA_PRIZE | typeof goldCharacterId): void
}>()

const { lingoSpinState, goldenPrizeCount } = useLingoGiveaway()
const baseGameCharacters = computed(() => gameCharacters.slice(0, -1) as {
  id: GACHA_PRIZE,
  image: string,
  name: string
}[])

const goldCharacter = computed(() => gameCharacters[gameCharacters.length - 1])

function getCharacterCount(characterId: GACHA_PRIZE) {
  if (!(characterId in lingoSpinState.value)) return 0
  return lingoSpinState.value[characterId] as number
}

function showCharacter(characterId: GACHA_PRIZE | typeof goldCharacterId) {
  if ((characterId === goldCharacterId && goldenPrizeCount.value !== 0) || (characterId !== goldCharacterId && getCharacterCount(characterId) !== 0)) {
    emit('showCard', characterId)
  }
}
</script>

<template>
  <div class="w-full flex px-2 items-stretch gap-x-2 min-[390px]:gap-x-3 min-[420px]:gap-x-4 justify-between">
    <div
      v-for="(character, index) in baseGameCharacters"
      :key="index"
      class="flex-1 max-w-[70px] flex mb-3"
      @click="showCharacter(character.id)"
    >
      <div
        v-if="getCharacterCount(character.id) === 0"
        class="w-full h-full flex items-end relative"
      >
        <img
          src="@/partners/lingo/lingo-giveaway/assets/images/egg-white.png"
          class="w-full h-auto object-cover"
        >
        <LingoGiveawayGameCounter
          :count="getCharacterCount(character.id)"
          class="absolute left-1/2 -translate-x-1/2 -bottom-1.5"
        />
      </div>
      <LingoGiveawayEggWithCharacter
        v-else
        :image="character.image"
        :count="getCharacterCount(character.id)"
      />
    </div>
    <div
      class="flex-1 max-w-[70px] flex mb-3 ml-4 mr-5 relative"
      @click="showCharacter(goldCharacterId)"
    >
      <div
        class="absolute golden-border p-px rounded-lg inset-0 z-[1] -left-4 -bottom-4 -top-[36px] w-[calc(100%+32px)] -right-4"
      >
        <div class="w-full h-full bg-gold-egg-card rounded-lg">
          <div class="mx-1 pt-2 text-white text-[7px] leading-[9px] font-medium -tracking-[0.065px] text-center">
            Guaranteed share
          </div>
          <div class="mx-px text-[9px] leading-[10px] font-semibold -tracking-[0.074px] text-center gold-text">
            ${{ formatNumberFullTruncate(winPrize) }} USDT
          </div>
        </div>
      </div>
      <img
        v-if="goldenPrizeCount === 0"
        src="@/partners/lingo/lingo-giveaway/assets/images/egg-gold.png"
        class="w-full h-auto object-cover z-[2]"
      >
      <LingoGiveawayEggWithCharacter
        v-else
        is-gold
        class="z-[6]"
        :image="goldCharacter.image"
        :count="goldenPrizeCount"
      />
      <div
        class="absolute z-[4] -left-4 -right-4 w-[calc(100%+32px)] -bottom-4 -top-[42px] min-[390px]:-top-[36px]"
      >
        <img
          src="@/partners/lingo/lingo-giveaway/assets/images/glow1.png"
          class="w-full h-full object-cover blur-[36px]"
        >
      </div>
      <LingoGiveawayGameCounter
        :count="goldenPrizeCount"
        class="z-[3] absolute left-1/2 -translate-x-1/2 -bottom-1.5"
        is-gold
      />
    </div>
  </div>
</template>

<style scoped>
.bg-character-card {
  box-shadow: 0px 2px 25px 0px rgba(0, 0, 0, 0.25) inset;
}

.card-glow {
  opacity: 0.7;
  filter: blur(2px);
}

.golden-border {
  background: linear-gradient(90deg, #FFCB00 0%, #B57800 22%, #B77C07 26%, #BF881B 31%, #CB9D3C 37%, #DCB96A 44%, #F1DDA5 52%, #FFF4C9 56%, #EACE88 62%, #D8AD4E 68%, #CB9423 73%, #C38509 77%, #C08000 79%, #C58500 81%, #EAB200 94%, #F9C300 100%);
  box-shadow: 0 0 5px 2px rgba(255, 203, 0, 0.5), 0 0 10px 4px rgba(255, 203, 0, 0.2);
}

.bg-gold-egg-card {
  background: linear-gradient(213deg, rgba(0, 0, 0, 0.00) 1.44%, rgba(244, 244, 244, 0.20) 29.97%, rgba(0, 0, 0, 0.20) 93.46%), #222;
}

.gold-text {
  background: linear-gradient(0deg, #CC9E36 -0.2%, #FFFF9F 53.8%, #CCA13B 99.8%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
</style>